/** Um IFC em texto, editável: ler entidades, alterá-las, criar novas e
 *  apagar sem deixar referências penduradas.
 *
 *  Porque não reescrever o ficheiro inteiro: um IFC de 150 MB tem
 *  milhões de linhas, e só mexemos em algumas centenas. O índice guarda
 *  onde começa e acaba cada entidade; a gravação copia o texto original
 *  e só troca as linhas alteradas, tira as apagadas e acrescenta as
 *  novas no fim da secção DATA. Tudo o resto sai byte a byte igual,
 *  incluindo o cabeçalho e as linhas que não percebemos.
 *
 *  Apagar é a parte delicada. Uma entidade apagada pode estar nomeada
 *  por outras, e uma referência para uma linha que já não existe
 *  estraga o ficheiro. A regra, a mesma que o IFClite usa no exportador
 *  de STEP deles (@ifc-lite/export, MPL-2.0; ideia, não código):
 *  - numa lista, a referência sai da lista;
 *  - uma relação (IfcRel...) que fica sem o objecto de que trata, ou
 *    com uma lista vazia, também é apagada;
 *  - noutra entidade, uma lista que fica vazia passa a `$`;
 *  - noutra entidade, uma referência simples bloqueia: essa entidade
 *    não se apaga e o relatório diz porquê.
 */

import { splitTopLevelArgs } from '../stepText'

export interface LinhaStep {
  id: number
  classe: string
  /** Índice do '#'. */
  inicio: number
  /** Índice logo a seguir ao ';'. */
  fim: number
  abre: number
  fecha: number
}

export interface Bloqueio {
  id: number
  por: number
  classeDeQuemReferencia: string
}

export interface ResultadoDeApagar {
  apagados: number[]
  bloqueados: Bloqueio[]
  /** Listas que ficaram vazias em entidades que não são relações e
   *  passaram a `$`. */
  esvaziados: { id: number; classe: string }[]
}

function indexar(texto: string): { linhas: Map<number, LinhaStep>; ordem: number[] } {
  const linhas = new Map<number, LinhaStep>()
  const ordem: number[] = []
  const n = texto.length
  // Começa na secção DATA: o cabeçalho também tem parênteses e plicas.
  const data = texto.search(/\bDATA\s*;/)
  let i = data >= 0 ? data : 0
  while (i < n) {
    const hash = texto.indexOf('#', i)
    if (hash < 0) break
    // Só conta um '#' em início de instrução (depois de ';' ou do DATA;),
    // o que se garante porque saltamos linha a linha a partir do ';'.
    let j = hash + 1
    let id = 0
    while (j < n) {
      const c = texto.charCodeAt(j)
      if (c < 48 || c > 57) break
      id = id * 10 + (c - 48)
      j++
    }
    if (j === hash + 1) {
      i = j
      continue
    }
    while (j < n && texto.charCodeAt(j) <= 32) j++
    if (texto[j] !== '=') {
      i = j
      continue
    }
    j++
    while (j < n && texto.charCodeAt(j) <= 32) j++
    const iniClasse = j
    while (j < n && texto[j] !== '(' && texto[j] !== ';') j++
    if (texto[j] !== '(') {
      i = j + 1
      continue
    }
    const classe = texto.slice(iniClasse, j).trim().toUpperCase()
    const abre = j
    // Fecho do parêntese, com atenção a plicas.
    let k = abre + 1
    let prof = 1
    let emTexto = false
    for (; k < n; k++) {
      const ch = texto[k]
      if (emTexto) {
        if (ch === "'") {
          if (texto[k + 1] === "'") k++
          else emTexto = false
        }
        continue
      }
      if (ch === "'") emTexto = true
      else if (ch === '(') prof++
      else if (ch === ')') {
        prof--
        if (prof === 0) break
      }
    }
    if (k >= n) break
    let pv = k + 1
    while (pv < n && texto[pv] !== ';') pv++
    linhas.set(id, { id, classe, inicio: hash, fim: pv + 1, abre, fecha: k })
    ordem.push(id)
    i = pv + 1
  }
  return { linhas, ordem }
}

/** Todos os ids referidos num texto de argumentos, fora de plicas. */
function refsEm(args: string): number[] {
  const out: number[] = []
  let emTexto = false
  for (let i = 0; i < args.length; i++) {
    const ch = args[i]
    if (emTexto) {
      if (ch === "'") {
        if (args[i + 1] === "'") i++
        else emTexto = false
      }
      continue
    }
    if (ch === "'") emTexto = true
    else if (ch === '#') {
      let j = i + 1
      let v = 0
      while (j < args.length && args.charCodeAt(j) >= 48 && args.charCodeAt(j) <= 57) {
        v = v * 10 + (args.charCodeAt(j) - 48)
        j++
      }
      if (j > i + 1) out.push(v)
      i = j - 1
    }
  }
  return out
}

/** Tira referências a `fora` de um argumento. Devolve o argumento novo,
 *  se havia uma referência simples (não numa lista) a um apagado, e se
 *  alguma lista ficou vazia. */
function limparArg(
  arg: string,
  fora: Set<number>,
): { arg: string; simples: boolean; esvaziou: boolean } {
  const t = arg.trim()
  const ref = /^#(\d+)$/.exec(t)
  if (ref) return { arg: t, simples: fora.has(parseInt(ref[1], 10)), esvaziou: false }
  if (t.startsWith('(') && t.endsWith(')')) {
    const interior = t.slice(1, -1).trim()
    if (interior === '') return { arg: t, simples: false, esvaziou: false }
    const itens = splitTopLevelArgs(interior).map((x) => x.trim())
    const ficam: string[] = []
    let simples = false
    let esvaziou = false
    for (const it of itens) {
      const r = /^#(\d+)$/.exec(it)
      if (r && fora.has(parseInt(r[1], 10))) continue
      const sub = limparArg(it, fora)
      simples ||= sub.simples
      esvaziou ||= sub.esvaziou
      ficam.push(sub.arg)
    }
    if (ficam.length === 0) return { arg: '()', simples, esvaziou: true }
    return { arg: `(${ficam.join(',')})`, simples, esvaziou }
  }
  // Valor tipado com referências lá dentro (raro, mas existe).
  const tip = /^([A-Za-z][A-Za-z0-9_]*)\s*\(([\s\S]*)\)$/.exec(t)
  if (tip) {
    const sub = limparArg(`(${tip[2]})`, fora)
    return { arg: `${tip[1]}${sub.arg}`, simples: sub.simples, esvaziou: sub.esvaziou }
  }
  return { arg: t, simples: false, esvaziou: false }
}

export class ModeloStep {
  readonly texto: string
  readonly linhas: Map<number, LinhaStep>
  private readonly ordem: number[]
  private readonly argsCache = new Map<number, string[]>()
  private readonly alterados = new Map<number, { classe: string; args: string[] }>()
  private readonly novos = new Map<number, { classe: string; args: string[] }>()
  private readonly apagados = new Set<number>()
  private porClasse: Map<string, number[]> | null = null
  private porGuidCache: Map<string, number> | null = null
  /** Quem referencia quem, em CSR: as origens de #n estão em
   *  `origens[inicio[n] .. inicio[n+1]]`. Construído quando se pede;
   *  qualquer edição o deita fora. */
  private inverso: { inicio: Int32Array; origens: Int32Array } | null = null
  private proximoId: number
  readonly quebra: string

  constructor(texto: string) {
    this.texto = texto
    const { linhas, ordem } = indexar(texto)
    this.linhas = linhas
    this.ordem = ordem
    let max = 0
    for (const id of ordem) if (id > max) max = id
    this.proximoId = max + 1
    this.quebra = texto.includes('\r\n') ? '\r\n' : '\n'
  }

  /** O esquema do cabeçalho (IFC2X3, IFC4, IFC4X3...). */
  esquema(): string | null {
    const m = /FILE_SCHEMA\s*\(\s*\(\s*'([^']+)'/i.exec(this.texto.slice(0, 4000))
    return m ? m[1].toUpperCase() : null
  }

  existe(id: number): boolean {
    if (this.apagados.has(id)) return false
    return this.linhas.has(id) || this.novos.has(id)
  }

  classe(id: number): string | undefined {
    if (this.apagados.has(id)) return undefined
    return this.novos.get(id)?.classe ?? this.alterados.get(id)?.classe ?? this.linhas.get(id)?.classe
  }

  /** Argumentos de topo, já sem espaços à volta. */
  args(id: number): string[] {
    const novo = this.novos.get(id) ?? this.alterados.get(id)
    if (novo) return novo.args
    const cached = this.argsCache.get(id)
    if (cached) return cached
    const l = this.linhas.get(id)
    if (!l) return []
    const a = splitTopLevelArgs(this.texto.slice(l.abre + 1, l.fecha)).map((x) => x.trim())
    this.argsCache.set(id, a)
    return a
  }

  /** Ids vivos de uma classe (em maiúsculas), por ordem do ficheiro. */
  idsDaClasse(classe: string): number[] {
    if (!this.porClasse) {
      this.porClasse = new Map()
      for (const id of this.ordem) {
        const c = this.linhas.get(id)!.classe
        const arr = this.porClasse.get(c)
        if (arr) arr.push(id)
        else this.porClasse.set(c, [id])
      }
    }
    const base = this.porClasse.get(classe.toUpperCase()) ?? []
    const out = base.filter((id) => !this.apagados.has(id) && this.classe(id) === classe.toUpperCase())
    for (const [id, n] of this.novos) if (n.classe === classe.toUpperCase() && !this.apagados.has(id)) out.push(id)
    return out
  }

  /** Todos os ids vivos, originais e novos. */
  *todos(): Iterable<number> {
    for (const id of this.ordem) if (!this.apagados.has(id)) yield id
    for (const id of this.novos.keys()) if (!this.apagados.has(id)) yield id
  }

  /** Id pela GlobalId (primeiro argumento de qualquer IfcRoot). */
  porGuid(guid: string): number | null {
    if (!this.porGuidCache) {
      this.porGuidCache = new Map()
      for (const id of this.ordem) {
        const l = this.linhas.get(id)!
        // Olhar só para o início poupa partir milhões de linhas.
        const ini = this.texto.slice(l.abre + 1, Math.min(l.abre + 30, l.fecha)).trimStart()
        if (ini[0] !== "'" || ini[23] !== "'") continue
        this.porGuidCache.set(ini.slice(1, 23), id)
      }
    }
    const id = this.porGuidCache.get(guid)
    return id !== undefined && this.existe(id) ? id : null
  }

  /** A linha STEP tal como está: a original, byte a byte, ou a reescrita. */
  linha(id: number): string | null {
    if (!this.existe(id)) return null
    const ed = this.novos.get(id) ?? this.alterados.get(id)
    if (ed) return this.linhaTexto(id, ed.classe, ed.args)
    const l = this.linhas.get(id)!
    return this.texto.slice(l.inicio, l.fim)
  }

  /** O texto do cabeçalho, de HEADER; ao primeiro ENDSEC;. */
  cabecalho(): string {
    const ini = this.texto.indexOf('HEADER;')
    if (ini < 0) return ''
    const fim = this.texto.indexOf('ENDSEC;', ini)
    return this.texto.slice(ini, fim < 0 ? ini : fim + 7)
  }

  /** Quantas entidades vivas há de cada classe, da mais numerosa para a menos. */
  contagemPorClasse(): [string, number][] {
    const n = new Map<string, number>()
    for (const id of this.todos()) {
      const c = this.classe(id)!
      n.set(c, (n.get(c) ?? 0) + 1)
    }
    return [...n].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  }

  /** Os ids para que esta entidade aponta, pela ordem em que aparecem. */
  referenciasDe(id: number): number[] {
    if (!this.existe(id)) return []
    const ed = this.novos.get(id) ?? this.alterados.get(id)
    if (ed) return refsEm(ed.args.join(','))
    const l = this.linhas.get(id)!
    return refsEm(this.texto.slice(l.abre + 1, l.fecha))
  }

  /** Quem aponta para esta entidade (sem repetidos, por ordem de id).
   *  A primeira chamada percorre o ficheiro todo uma vez. */
  referenciadoPor(id: number): number[] {
    const inv = (this.inverso ??= this.construirInverso())
    if (id < 0 || id + 1 >= inv.inicio.length) return []
    const out: number[] = []
    let antes = -1
    for (let k = inv.inicio[id]; k < inv.inicio[id + 1]; k++) {
      const o = inv.origens[k]
      if (o !== antes && this.existe(o)) out.push(o)
      antes = o
    }
    return out
  }

  private construirInverso(): { inicio: Int32Array; origens: Int32Array } {
    // Pares (origem, destino) em arrays que crescem; depois uma
    // contagem por destino. Num IFC de milhões de linhas, Maps de
    // arrays custavam centenas de MB.
    let de = new Int32Array(1 << 16)
    let para = new Int32Array(1 << 16)
    let n = 0
    let max = this.proximoId
    for (const id of this.todos()) {
      for (const r of this.referenciasDe(id)) {
        if (n === de.length) {
          const d2 = new Int32Array(n * 2)
          d2.set(de)
          de = d2
          const p2 = new Int32Array(n * 2)
          p2.set(para)
          para = p2
        }
        de[n] = id
        para[n] = r
        n++
        if (r > max) max = r
      }
    }
    const inicio = new Int32Array(max + 2)
    for (let k = 0; k < n; k++) inicio[para[k] + 1]++
    for (let i = 1; i < inicio.length; i++) inicio[i] += inicio[i - 1]
    const cursor = inicio.slice(0, max + 1)
    const origens = new Int32Array(n)
    // As origens saem por ordem de id porque todos() as dá por ordem do
    // ficheiro; ordenar cada fatia garante-o também para as novas.
    for (let k = 0; k < n; k++) origens[cursor[para[k]]++] = de[k]
    for (let i = 0; i <= max; i++) {
      if (inicio[i + 1] - inicio[i] > 1) origens.subarray(inicio[i], inicio[i + 1]).sort()
    }
    return { inicio, origens }
  }

  definirArgs(id: number, args: string[]): void {
    this.inverso = null
    if (this.novos.has(id)) {
      this.novos.get(id)!.args = args
      return
    }
    const l = this.linhas.get(id)
    if (!l || this.apagados.has(id)) throw new Error(`#${id} não existe.`)
    this.alterados.set(id, { classe: this.classe(id)!, args })
  }

  definirArg(id: number, indice: number, valor: string): void {
    const a = [...this.args(id)]
    a[indice] = valor
    this.definirArgs(id, a)
  }

  criar(classe: string, args: string[]): number {
    this.inverso = null
    const id = this.proximoId++
    this.novos.set(id, { classe: classe.toUpperCase(), args })
    return id
  }

  /** Quem referencia algum dos ids dados (sem contar os já apagados). */
  quemReferencia(ids: Set<number>): number[] {
    const out: number[] = []
    for (const id of this.todos()) {
      if (ids.has(id)) continue
      const bruto = this.novos.get(id) ?? this.alterados.get(id)
      const refs = bruto
        ? refsEm(bruto.args.join(','))
        : (() => {
            const l = this.linhas.get(id)!
            return refsEm(this.texto.slice(l.abre + 1, l.fecha))
          })()
      if (refs.some((r) => ids.has(r))) out.push(id)
    }
    return out
  }

  /** Apaga os ids pedidos e limpa as referências a eles. Ver as regras
   *  no topo do ficheiro. Nada é apagado de um pedido que fique
   *  bloqueado; o resto avança. */
  apagar(pedidos: Iterable<number>): ResultadoDeApagar {
    let pedido = new Set([...pedidos].filter((id) => this.existe(id)))
    const bloqueados: Bloqueio[] = []
    while (pedido.size > 0) {
      const plano = this.planearApagar(pedido)
      if (plano.bloqueados.length === 0) {
        this.executarPlano(plano)
        return { apagados: [...plano.fecho], bloqueados, esvaziados: plano.esvaziados }
      }
      // Tira os pedidos bloqueados e volta a planear sem eles.
      bloqueados.push(...plano.bloqueados)
      const ids = new Set(plano.bloqueados.map((b) => b.id))
      const resto = new Set([...pedido].filter((id) => !ids.has(id)))
      // Bloqueio numa entidade que só entrou em cascata: não há pedido
      // a retirar, e apagar o resto deixaria referências penduradas.
      if (resto.size === pedido.size) return { apagados: [], bloqueados, esvaziados: [] }
      pedido = resto
    }
    return { apagados: [], bloqueados, esvaziados: [] }
  }

  private planearApagar(pedido: Set<number>) {
    const fecho = new Set(pedido)
    const bloqueados: Bloqueio[] = []
    const edicoes = new Map<number, string[]>()
    const esvaziados: { id: number; classe: string }[] = []
    let fronteira = new Set(pedido)
    while (fronteira.size > 0) {
      const seguinte = new Set<number>()
      for (const quem of this.quemReferencia(fecho)) {
        if (fecho.has(quem)) continue
        const classe = this.classe(quem)!
        const args = edicoes.get(quem) ?? this.args(quem)
        let simples = false
        let esvaziou = false
        const novos = args.map((a) => {
          const r = limparArg(a, fecho)
          simples ||= r.simples
          esvaziou ||= r.esvaziou
          return r.arg
        })
        if (!novos.some((a, i) => a !== args[i]) && !simples) continue
        if (classe.startsWith('IFCREL')) {
          if (simples || esvaziou) {
            fecho.add(quem)
            seguinte.add(quem)
            edicoes.delete(quem)
            continue
          }
          edicoes.set(quem, novos)
          continue
        }
        if (simples) {
          // Descobrir qual dos pedidos é referido de forma simples.
          for (const a of args) {
            const r = /^#(\d+)$/.exec(a.trim())
            if (r && fecho.has(parseInt(r[1], 10))) {
              bloqueados.push({ id: parseInt(r[1], 10), por: quem, classeDeQuemReferencia: classe })
            }
          }
          continue
        }
        if (esvaziou) esvaziados.push({ id: quem, classe })
        edicoes.set(quem, novos.map((a) => (a === '()' ? '$' : a)))
      }
      fronteira = seguinte
    }
    return { fecho, bloqueados, edicoes, esvaziados }
  }

  private executarPlano(plano: { fecho: Set<number>; edicoes: Map<number, string[]> }): void {
    this.inverso = null
    for (const [id, args] of plano.edicoes) this.definirArgs(id, args)
    for (const id of plano.fecho) {
      this.apagados.add(id)
      this.alterados.delete(id)
      this.novos.delete(id)
    }
  }

  /** Houve alguma alteração? */
  alterado(): boolean {
    return this.alterados.size > 0 || this.novos.size > 0 || this.apagados.size > 0
  }

  resumo(): { alterados: number; criados: number; apagados: number } {
    return { alterados: this.alterados.size, criados: this.novos.size, apagados: this.apagados.size }
  }

  private linhaTexto(id: number, classe: string, args: string[]): string {
    return `#${id}=${classe}(${args.join(',')});`
  }

  /** O ficheiro com as alterações. */
  serializar(): string {
    const trocas: { inicio: number; fim: number; texto: string }[] = []
    for (const [id, a] of this.alterados) {
      const l = this.linhas.get(id)!
      trocas.push({ inicio: l.inicio, fim: l.fim, texto: this.linhaTexto(id, a.classe, a.args) })
    }
    for (const id of this.apagados) {
      const l = this.linhas.get(id)
      if (!l) continue
      // Leva a quebra de linha, para não ficarem linhas em branco.
      let fim = l.fim
      if (this.texto[fim] === '\r') fim++
      if (this.texto[fim] === '\n') fim++
      trocas.push({ inicio: l.inicio, fim, texto: '' })
    }
    trocas.sort((a, b) => a.inicio - b.inicio)
    const partes: string[] = []
    let cursor = 0
    for (const t of trocas) {
      partes.push(this.texto.slice(cursor, t.inicio), t.texto)
      cursor = t.fim
    }
    partes.push(this.texto.slice(cursor))
    let out = partes.join('')
    if (this.novos.size > 0) {
      const novas = [...this.novos].map(([id, n]) => this.linhaTexto(id, n.classe, n.args)).join(this.quebra)
      // O ENDSEC que fecha a secção DATA é o último do ficheiro.
      const fimData = out.lastIndexOf('ENDSEC;')
      if (fimData < 0) throw new Error('O ficheiro não tem o ENDSEC da secção DATA.')
      out = out.slice(0, fimData) + novas + this.quebra + out.slice(fimData)
    }
    return out
  }
}
